import { useCallback } from 'react';

import { useSession, updateUser } from '@erikdakoda/auth/client/betterAuthClient';
import type { SessionQueryParams } from 'better-auth/client';
import type { AuthUser, CurrentUserContext } from '@erikdakoda/auth/types';

// https://www.better-auth.com/docs/concepts/users-accounts

export function useCurrentUser(): CurrentUserContext {
  const { data: session, isPending, error, refetch: refetchSession } = useSession();

  const refetchCurrentUser = useCallback(
    (queryParams: { query?: SessionQueryParams } = {}): void | Promise<void> => {
      return refetchSession({
        query: {
          disableCookieCache: true,
          ...queryParams.query,
        },
      });
    },
    [refetchSession],
  );

  if (isPending) {
    return {
      user: null,
      expires: '',
      status: 'loading',
      isUserLoading: true,
      isAuthenticated: false,
      isAdmin: false,
      error,
      refetchCurrentUser,
      updateCurrentUser: updateUser,
    };
  }

  if (session?.user) {
    return {
      user: session.user as AuthUser,
      expires: session.session.expiresAt.toISOString(),
      status: 'authenticated',
      isUserLoading: false,
      isAuthenticated: true,
      isAdmin: ['ADMIN', 'TEAM'].includes(session.user.role) && !session.user.disableAdmin,
      error,
      refetchCurrentUser,
      updateCurrentUser: updateUser,
    };
  }

  return {
    user: null,
    expires: '',
    status: 'unauthenticated',
    isUserLoading: false,
    isAuthenticated: false,
    isAdmin: false,
    error,
    refetchCurrentUser,
    updateCurrentUser: updateUser,
  };
}
